"use client";

import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Shield, HeartPulse, Stethoscope, Scan, Sparkles, Hammer, Pill, Syringe, Bone, Brush, Smile, Waves, BadgeCheck, Droplets, AlertTriangle, Layers, Replace } from "lucide-react";
const APPOINTMENT_URL = "https://www.appointnow.com/?P=4826&O=101&PT=0&campaignID=";
type Service = {
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  slug?: string; // optional internal anchor / route later
};
export default function AllDentalServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const services: Service[] = [{
    icon: Brush,
    title: "Dental Cleanings",
    description: "Professional cleanings remove plaque and tartar to help prevent cavities, gum disease, and bad breath."
  }, {
    icon: Stethoscope,
    title: "Comprehensive Exams",
    description: "A complete evaluation of teeth, gums, bite, and oral health—ideal for new patients and routine checkups."
  }, {
    icon: Shield,
    title: "Digital X-Rays",
    description: "Low-radiation imaging to detect cavities, infections, bone changes, and other issues early—before they worsen."
  }, {
    icon: Layers,
    title: "Oral Cancer Screening",
    description: "Quick screening during exams to check for suspicious areas and support early detection."
  }, {
    icon: Replace,
    title: "Tooth-Colored Fillings",
    description: "Natural-looking composite fillings to repair cavities and minor tooth damage while preserving appearance."
  }, {
    icon: Hammer,
    title: "Crowns",
    description: "Custom crowns restore strength and function to cracked, weakened, or heavily filled teeth."
  }, {
    icon: BadgeCheck,
    title: "Bridges",
    description: "Fixed replacements that fill gaps from missing teeth—improving chewing, speech, and smile balance."
  }, {
    icon: Sparkles,
    title: "Teeth Whitening",
    description: "Professional whitening options to brighten your smile safely and effectively."
  }, {
    icon: Waves,
    title: "Gum Disease Treatment",
    description: "Deep cleanings and periodontal care to reduce inflammation, bleeding, and gum infection."
  }, {
    icon: Droplets,
    title: "Fluoride & Prevention",
    description: "Fluoride treatments and preventive guidance to strengthen enamel and lower cavity risk."
  }, {
    icon: Pill,
    title: "Night Guards",
    description: "Custom guards for clenching/grinding (bruxism) to protect teeth, reduce jaw tension, and prevent wear."
  }, {
    icon: Smile,
    title: "Dentures",
    description: "Full or partial dentures designed for comfort and function to replace missing teeth and restore confidence."
  }, {
    icon: Syringe,
    title: "Tooth Extractions",
    description: "Gentle removal of problem teeth when saving the tooth isn’t possible—simple and surgical extractions available."
  }, {
    icon: Bone,
    title: "Wisdom Teeth Evaluation",
    description: "Assessment of third molars for pain, crowding, infection, or impaction—referrals when advanced surgery is needed."
  }, {
    icon: HeartPulse,
    title: "Emergency Dentistry",
    description: "Same-day help for toothaches, swelling, broken teeth, and urgent dental concerns whenever possible."
  }, {
    icon: AlertTriangle,
    title: "Root Canal Therapy",
    description: "Treatment to remove infection inside the tooth, relieve pain, and help you keep your natural tooth."
  }, {
    icon: Scan,
    title: "Sealants",
    description: "Protective coating for back teeth grooves—helps reduce cavity risk, especially for kids and teens."
  }];

  // Optional: lightweight ItemList schema (helps search understand this is a services list)
  const servicesJsonLd = useMemo(() => {
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: services.map((s, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: s.title,
        description: s.description
      }))
    };
    return JSON.stringify(itemList);
  }, [services]);
  return <section className="bg-light-background py-16 px-4 sm:px-8" aria-labelledby="all-services-heading">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: servicesJsonLd
    }} />

      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h2 id="all-services-heading" className="font-display text-3xl font-bold text-foreground-light sm:text-4xl">
            General Dentistry Services
          </h2>
          <p className="text-muted-foreground mt-3 text-base">
            Comprehensive care for every stage of life—from prevention to restoring teeth and relieving pain.
          </p>
        </div>

        <div ref={ref} className={`transition-all duration-200 ease-[var(--ease-smooth)] ${isVisible ? "animate-fade-in-up opacity-100" : "opacity-0"}`}>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <Card key={`${service.title}-${i}`} className="group h-full border border-border bg-muted-light-background shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-0.5 hover:border-secondary hover:shadow-medium">
                  <CardHeader>
                    <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary-light text-primary">
                      <Icon className="size-6" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground-light">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>

                  <CardFooter className="mt-auto">
                    <Button asChild variant="default" size="default" className="w-full bg-primary text-foreground-primary hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-ring">
                      <Link to={APPOINTMENT_URL} newTab aria-label={`Schedule an appointment for ${service.title}`}>
                        Schedule
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>;
          })}
          </div>

          <div className="mt-10 text-center">
            <p className="text-muted-foreground text-sm">
              Looking for cosmetic or specialty care? Visit our{" "}
              <Link to="/services/" className="underline underline-offset-4">
                Services
              </Link>{" "}
              page or call{" "}
              <a href="tel:2107755177" className="underline underline-offset-4">
                (210) 775-5177
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>;
}